import { api } from './api'
import { WaMessage, WaConversation, WaMessageType, PaginatedResponse } from '@/types'

export interface WaConversationFilters {
  accountId?: number
  search?: string
  isArchived?: boolean
  isPinned?: boolean
  hasUnread?: boolean
  page?: number
  limit?: number
}

export interface WaMessageFilters {
  conversationId?: number
  contactId?: number
  accountId?: number
  direction?: 'INBOUND' | 'OUTBOUND'
  type?: WaMessageType
  page?: number
  limit?: number
}

export interface SendMessageRequest {
  accountId: number
  contactId: number
  type: WaMessageType
  content: string
  mediaUrl?: string
  templateId?: number
  templateVariables?: Record<string, string>
}

export interface SendBulkMessageRequest {
  accountId: number
  contactIds: number[]
  type: WaMessageType
  content: string
  mediaUrl?: string
  templateId?: number
  templateVariables?: Record<string, string>
}

export const waMessageService = {
  // Conversations (Inbox)
  async fetchConversations(filters: WaConversationFilters = {}): Promise<PaginatedResponse<WaConversation>> {
    const response = await api.get<{
      success: boolean
      data: WaConversation[]
      pagination: {
        page: number
        limit: number
        total: number
        totalPages: number
      }
    }>('/whatsapp/messages/conversations', { params: filters })
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 },
    }
  },

  async getConversation(id: number): Promise<WaConversation> {
    const response = await api.get<{ success: boolean; data: WaConversation }>(
      `/whatsapp/messages/conversations/${id}`
    )
    return response.data.data
  },

  async archiveConversation(id: number): Promise<WaConversation> {
    const response = await api.post<{ success: boolean; data: WaConversation }>(
      `/whatsapp/messages/conversations/${id}/archive`
    )
    return response.data.data
  },

  async unarchiveConversation(id: number): Promise<WaConversation> {
    const response = await api.post<{ success: boolean; data: WaConversation }>(
      `/whatsapp/messages/conversations/${id}/unarchive`
    )
    return response.data.data
  },

  async pinConversation(id: number): Promise<WaConversation> {
    const response = await api.post<{ success: boolean; data: WaConversation }>(
      `/whatsapp/messages/conversations/${id}/pin`
    )
    return response.data.data
  },

  async unpinConversation(id: number): Promise<WaConversation> {
    const response = await api.post<{ success: boolean; data: WaConversation }>(
      `/whatsapp/messages/conversations/${id}/unpin`
    )
    return response.data.data
  },

  async markConversationAsRead(id: number): Promise<void> {
    await api.post(`/whatsapp/messages/conversations/${id}/read`)
  },

  // Messages
  async fetchMessages(filters: WaMessageFilters = {}): Promise<PaginatedResponse<WaMessage>> {
    const response = await api.get<{
      success: boolean
      data: WaMessage[]
      pagination: {
        page: number
        limit: number
        total: number
        totalPages: number
      }
    }>('/whatsapp/messages', { params: filters })
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 },
    }
  },

  async getMessage(id: number): Promise<WaMessage> {
    const response = await api.get<{ success: boolean; data: WaMessage }>(`/whatsapp/messages/${id}`)
    return response.data.data
  },

  async sendMessage(data: SendMessageRequest): Promise<WaMessage> {
    const response = await api.post<{ success: boolean; data: WaMessage }>('/whatsapp/messages/send', data)
    return response.data.data
  },

  async sendBulkMessages(data: SendBulkMessageRequest): Promise<{ sent: number; failed: number }> {
    const response = await api.post<{ success: boolean; data: { sent: number; failed: number } }>(
      '/whatsapp/messages/send-bulk',
      data
    )
    return response.data.data
  },

  async getConversationMessages(
    contactId: number,
    accountId: number,
    params?: { page?: number; limit?: number }
  ): Promise<PaginatedResponse<WaMessage>> {
    const response = await api.get<{
      success: boolean
      data: WaMessage[]
      pagination: any
    }>(`/whatsapp/messages/chat/${contactId}`, {
      params: { accountId, ...params },
    })
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 },
    }
  },

  // Inbox stats
  async getInboxStats(accountId?: number): Promise<{
    totalConversations: number
    unreadCount: number
    todayMessages: number
    activeChats: number
  }> {
    const response = await api.get<{ success: boolean; data: any }>('/whatsapp/messages/stats', {
      params: { accountId },
    })
    return response.data.data
  },

  // Contact profile picture
  async getContactProfilePicture(
    contactId: number,
    accountId?: number
  ): Promise<{ profilePictureUrl: string | null }> {
    const response = await api.get<{ success: boolean; data: { profilePictureUrl: string | null } }>(
      `/whatsapp/contacts/${contactId}/profile-picture`,
      { params: { accountId } }
    )
    return response.data.data
  },
}
